import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, TrendingDown, Trophy, Users, Clock } from "lucide-react";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useCurrentOrgId } from "@/hooks/useSkillOS";

type Poll = {
  id: string;
  question: string;
  options: { label: string; votes: number }[];
  decay_rate: number;
  created_at: string;
  closed_at: string | null;
  total_votes: number;
  group_id: string | null;
};

type GroupRow = { id: string; name: string };

const LS_KEY = "polls.selectedGroupId";

export default function Polls() {
  const { data: orgId } = useCurrentOrgId();
  const [selectedGroupId, setSelectedGroupId] = useState<string>(() => {
    try { return localStorage.getItem(LS_KEY) || "all"; } catch { return "all"; }
  });

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, selectedGroupId); } catch { /* noop */ }
  }, [selectedGroupId]);

  const { data: groups = [] } = useQuery({
    queryKey: ["polls-groups", orgId ?? "no-org"],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name")
        .eq("org_id", orgId!)
        .eq("active", true)
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as GroupRow[];
    },
  });

  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    groups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const { data: polls = [], isLoading } = useQuery({
    queryKey: ["polls", orgId ?? "no-org", selectedGroupId],
    enabled: !!orgId,
    queryFn: async () => {
      let q = supabase
        .from("syra_polls")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (selectedGroupId !== "all") q = q.eq("group_id", selectedGroupId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as Poll[];
    },
  });

  const decayPct = (created_at: string, decay_rate: number) => {
    const ageH = (Date.now() - new Date(created_at).getTime()) / 3600000;
    const remaining = Math.exp(-decay_rate * ageH);
    return Math.max(0, Math.min(1, remaining));
  };

  const ageLabel = (created_at: string) => {
    const ms = Date.now() - new Date(created_at).getTime();
    const h = Math.floor(ms / 3600000);
    if (h < 1) return `${Math.floor(ms / 60000)}min atrás`;
    if (h < 24) return `${h}h atrás`;
    return `${Math.floor(h / 24)}d atrás`;
  };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Enquetes" }]} />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-headline flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" /> Enquetes
          </h1>
          <p className="text-sm text-muted-foreground">
            Enquetes do grupo com peso decaindo com o tempo (decay temporal).
          </p>
        </div>
        <div className="w-full md:w-72">
          <label className="text-xs text-muted-foreground mb-1 block">Filtrar por grupo</label>
          <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
            <SelectTrigger><SelectValue placeholder="Todos os grupos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os grupos</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : polls.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Nenhuma enquete ainda.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {polls.map((p) => {
            const opts = p.options ?? [];
            const total = p.total_votes || opts.reduce((s, o) => s + o.votes, 0) || 0;
            const totalSafe = total || 1;
            const decay = decayPct(p.created_at, p.decay_rate);
            const maxVotes = opts.reduce((m, o) => Math.max(m, o.votes), 0);
            const groupName = p.group_id ? groupNameById.get(p.group_id) : null;

            return (
              <Card key={p.id}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{p.question}</CardTitle>
                    {p.closed_at ? <Badge variant="secondary">fechada</Badge> : <Badge>ativa</Badge>}
                  </div>
                  <CardDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {groupName && <span className="font-medium text-foreground">{groupName}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {total} voto{total === 1 ? "" : "s"}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {ageLabel(p.created_at)}</span>
                    <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3" /> peso {(decay * 100).toFixed(0)}%</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {opts.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sem alternativas registradas.</p>
                  ) : (
                    opts.map((opt, i) => {
                      const pct = (opt.votes / totalSafe) * 100;
                      const isLeader = total > 0 && opt.votes === maxVotes && opt.votes > 0;
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm flex items-center gap-1.5 ${isLeader ? "font-semibold text-primary" : ""}`}>
                              {isLeader && <Trophy className="w-3.5 h-3.5" />}
                              {opt.label}
                              {isLeader && <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4">líder</Badge>}
                            </span>
                            <span className={`text-sm tabular-nums ${isLeader ? "font-semibold text-primary" : "text-muted-foreground"}`}>
                              {opt.votes} <span className="text-xs">({pct.toFixed(0)}%)</span>
                            </span>
                          </div>
                          <Progress value={pct} className={`h-2 ${isLeader ? "" : "opacity-70"}`} />
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
